/**
 * PortfolioDemoPage — /portfolio-kit/demo
 *
 * The "Jordan Martinez, Staff AI Engineer" exemplar portfolio.
 * A fictional composite persona built to show what the Lovable prompt kit produces.
 *
 * Fully public — serves as both a demo and a quality bar reference.
 * Uses dark theme in-page to match the Lovable output aesthetic.
 */

import {
  Box,
  Button,
  Chip,
  Container,
  Paper,
  Stack,
  Typography,
  Divider,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import GitHubIcon from "@mui/icons-material/GitHub";
import LaunchIcon from "@mui/icons-material/Launch";
import BarChartIcon from "@mui/icons-material/BarChart";
import { useNavigate } from "react-router-dom";
import { Trans, useTranslation } from "react-i18next";
import { EXEMPLAR_PERSONA } from "../data/portfolioKit";
import { L } from "../utils/localized";

// coral via theme.palette.primary — per-product accent deferred (spec D2)

// Exemplar project metrics — these are the concrete numbers Jordan claims, aligned with our archetype eval metrics.
const EXEMPLAR_PROJECTS = [
  {
    slug: "rag-agent",
    title: "Agentic RAG — AI Safety Research",
    hook: "Agentic RAG over 5,012 AI safety papers. Planner + grader + rewriter + groundedness check. 30-query eval set, traces public.",
    headlineMetric: "84% grounded-answer rate",
    tags: ["LangGraph", "pgvector", "Langfuse", "Claude + Haiku"],
    githubUrl: "https://github.com/jordan-martinez/safety-rag",
    demoUrl: "https://safety-rag.jordanm.dev",
    tracesUrl: "https://langfuse.com/public/jordan-m/safety-rag",
    loomUrl: "https://loom.com/share/jordan-safety-rag",
    whyItMatters:
      "Demonstrates agentic RAG pattern (D2), tool choice across 3 retrievers (D3), rigorous eval (D4), and refusal-on-out-of-corpus safety (D6).",
  },
  {
    slug: "multi-agent-researcher",
    title: "LeadResearcher — Anthropic Pattern Implementation",
    hook: "Multi-agent research using Anthropic's LeadResearcher + Subagent pattern. 20-task benchmark with completeness rubric.",
    headlineMetric: "3.8× parallelism gain vs single-agent baseline",
    tags: ["Async orchestration", "OpenTelemetry", "Brave Search", "Postgres memory"],
    githubUrl: "https://github.com/jordan-martinez/lead-researcher",
    demoUrl: "https://research.jordanm.dev",
    tracesUrl: "https://honeycomb.io/public/jordan-m/lead-researcher",
    loomUrl: "https://loom.com/share/jordan-lead-researcher",
    whyItMatters:
      "D2 (pattern choice justified), D3 (per-subagent tool scoping), D5 (shared memory + gap-loop convergence), D6 (subagent-spawn restrictions).",
  },
  {
    slug: "hitl-workflow-agent",
    title: "GitHub Workflow Agent with HITL Gates",
    hook: "Action-taking agent that opens PRs on real repos — with blast-radius classifier + 2-step approval for irreversible actions.",
    headlineMetric: "0% approval-gate miss on 30-intent adversarial eval",
    tags: ["FastAPI", "Slack approval bot", "Postgres audit log", "Claude function-calling"],
    githubUrl: "https://github.com/jordan-martinez/gh-workflow-agent",
    demoUrl: "https://github.com/jordan-martinez/gh-workflow-agent/tree/main/traces",
    tracesUrl: "https://langfuse.com/public/jordan-m/gh-workflow",
    loomUrl: "https://loom.com/share/jordan-gh-agent",
    whyItMatters:
      "D3 (real tool integration with scoped creds), D5 (audit log + rollback design), D6 (adversarial eval — this is where D6 lives), D7 (approval UX without fatigue).",
  },
];

export default function PortfolioDemoPage() {
  const { t, i18n } = useTranslation("pages-products");
  const theme = useTheme();
  const navigate = useNavigate();
  const persona = L(EXEMPLAR_PERSONA, i18n.language);
  // Page-scoped dark-demo palette derived from theme tokens.
  const DARK_BG = theme.palette.background.paper;
  const DARK_SURFACE = theme.palette.background.default;
  const DARK_TEXT = theme.palette.background.default;
  const DARK_MUTED = theme.palette.text.disabled;
  const ACCENT = theme.palette.primary.main;

  return (
    <Box sx={{ bgcolor: DARK_BG, minHeight: "100vh", color: DARK_TEXT }}>
      {/* CareerForge-contextual banner — reminds user this is a demo */}
      <Box
        sx={{
          bgcolor: alpha(theme.palette.primary.main, 0.15),
          borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
          py: 1.2,
        }}
      >
        <Container maxWidth="lg">
          <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
            <InfoOutlinedIcon sx={{ fontSize: 18, color: ACCENT }} />
            <Typography variant="body2" sx={{ color: DARK_TEXT, fontSize: "0.85rem" }}>
              <Trans
                i18nKey="PortfolioDemoPage.bannerNotice"
                t={t}
                components={[
                  <strong key="strong0" />,
                  <Box
                    component="span"
                    key="span1"
                    onClick={() => navigate("/portfolio-kit/lovable-prompt")}
                    sx={{
                      color: ACCENT,
                      cursor: "pointer",
                      textDecoration: "underline",
                      fontWeight: 500,
                      "&:hover": { color: alpha(ACCENT, 0.85) },
                    }}
                  />,
                ]}
              />
            </Typography>
            <Box sx={{ flex: 1 }} />
            <Button
              size="small"
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate("/portfolio-kit")}
              sx={{ color: alpha(DARK_TEXT, 0.9), textTransform: "none" }}
            >
              {t("PortfolioDemoPage.backToPortfolioKitButton")}
            </Button>
          </Stack>
        </Container>
      </Box>

      {/* Hero */}
      <Container maxWidth="lg" sx={{ py: { xs: 8, md: 12 } }}>
        <Stack spacing={3} alignItems="flex-start">
          <Stack direction="row" spacing={1} flexWrap="wrap" gap={0.5}>
            <Chip
              label={persona.band.toUpperCase()}
              size="small"
              sx={{
                bgcolor: alpha(ACCENT, 0.15),
                color: ACCENT,
                fontWeight: 500,
                letterSpacing: "0.08em",
                fontSize: "0.72rem",
              }}
            />
            <Chip
              label={t("PortfolioDemoPage.agenticSystemsChipLabel")}
              size="small"
              sx={{ bgcolor: alpha(DARK_TEXT, 0.08), color: DARK_MUTED, fontWeight: 500, fontSize: "0.72rem" }}
            />
            <Chip
              label={t("PortfolioDemoPage.openToStaffPrincipalChipLabel")}
              size="small"
              sx={{ bgcolor: alpha(ACCENT, 0.08), color: ACCENT, fontWeight: 500, fontSize: "0.72rem" }}
            />
          </Stack>
          <Typography
            variant="h1"
            fontWeight={500}
            sx={{
              fontFamily: "'Fraunces', Georgia, serif",
              fontSize: { xs: "3rem", md: "4.5rem" },
              letterSpacing: "-0.03em",
              lineHeight: 1.05,
              color: DARK_TEXT,
              maxWidth: 900,
            }}
          >
            {persona.name}
          </Typography>
          <Typography
            variant="h4"
            sx={{
              color: alpha(DARK_TEXT, 0.8),
              fontWeight: 400,
              maxWidth: 780,
              fontSize: { xs: "1.4rem", md: "1.75rem" },
              lineHeight: 1.35,
            }}
          >
            {persona.title} ·{" "}
            <Box component="span" sx={{ color: ACCENT, fontWeight: 500 }}>
              {persona.focus}
            </Box>
          </Typography>
          <Typography
            variant="body1"
            sx={{ color: alpha(DARK_TEXT, 0.75), maxWidth: 820, lineHeight: 1.7, fontSize: "1.05rem" }}
          >
            {persona.positioning}
          </Typography>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} mt={2}>
            <Button
              variant="contained"
              size="large"
              sx={{
                bgcolor: ACCENT,
                color: DARK_BG,
                fontWeight: 500,
                px: 3,
                "&:hover": { bgcolor: alpha(ACCENT, 0.85) },
              }}
            >
              {t("PortfolioDemoPage.startHonestFitAssessmentButton")}
            </Button>
            <Button
              variant="outlined"
              size="large"
              sx={{
                borderColor: alpha(ACCENT, 0.5),
                color: ACCENT,
                fontWeight: 500,
                px: 3,
                "&:hover": { borderColor: ACCENT, bgcolor: alpha(ACCENT, 0.06) },
              }}
            >
              {t("PortfolioDemoPage.analyzeJobDescriptionButton")}
            </Button>
          </Stack>
        </Stack>
      </Container>

      {/* Projects */}
      <Box sx={{ bgcolor: DARK_SURFACE, py: { xs: 6, md: 10 } }}>
        <Container maxWidth="lg">
          <Stack spacing={1} mb={4}>
            <Typography
              variant="overline"
              fontWeight={700}
              sx={{ color: ACCENT, letterSpacing: "0.08em" }}
            >
              {t("PortfolioDemoPage.projectsWithMetricsEyebrow")}
            </Typography>
            <Typography
              variant="h3"
              fontWeight={500}
              sx={{
                fontFamily: "'Fraunces', Georgia, serif",
                letterSpacing: "-0.02em",
                color: DARK_TEXT,
              }}
            >
              {t("PortfolioDemoPage.projectsWithMetricsTitle")}
            </Typography>
          </Stack>
          <Stack spacing={3}>
            {EXEMPLAR_PROJECTS.map((project) => (
              <Paper
                key={project.slug}
                sx={{
                  p: { xs: 3, md: 4 },
                  bgcolor: alpha(DARK_TEXT, 0.03),
                  border: `1px solid ${alpha(ACCENT, 0.15)}`,
                  borderRadius: 3,
                  "&:hover": { borderColor: alpha(ACCENT, 0.4) },
                  transition: "all 0.2s",
                }}
              >
                <Stack direction={{ xs: "column", md: "row" }} spacing={3} alignItems="flex-start">
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      variant="h5"
                      fontWeight={500}
                      sx={{ color: DARK_TEXT, mb: 1, fontFamily: "'Fraunces', Georgia, serif" }}
                    >
                      {project.title}
                    </Typography>
                    <Typography variant="body2" sx={{ color: alpha(DARK_TEXT, 0.75), lineHeight: 1.6, mb: 2 }}>
                      {project.hook}
                    </Typography>
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" gap={0.5} mb={2}>
                      {project.tags.map((tag) => (
                        <Chip
                          key={tag}
                          label={tag}
                          size="small"
                          sx={{
                            bgcolor: alpha(DARK_TEXT, 0.06),
                            color: alpha(DARK_TEXT, 0.8),
                            fontSize: "0.72rem",
                            fontFamily: "monospace",
                          }}
                        />
                      ))}
                    </Stack>
                    <Stack direction="row" spacing={1.5} flexWrap="wrap" gap={0.5} mb={2}>
                      <LinkButton icon={<GitHubIcon sx={{ fontSize: 16 }} />} label={t("PortfolioDemoPage.githubLinkLabel")} />
                      <LinkButton icon={<LaunchIcon sx={{ fontSize: 16 }} />} label={t("PortfolioDemoPage.liveDemoLinkLabel")} />
                      <LinkButton icon={<BarChartIcon sx={{ fontSize: 16 }} />} label={t("PortfolioDemoPage.publicTracesLinkLabel")} />
                      <LinkButton icon={<OpenInNewIcon sx={{ fontSize: 16 }} />} label={t("PortfolioDemoPage.loomWalkthroughLinkLabel")} />
                    </Stack>
                    <Typography variant="caption" sx={{ color: alpha(DARK_TEXT, 0.55), lineHeight: 1.6, fontStyle: "italic" }}>
                      {t("PortfolioDemoPage.whyItMattersLabel")}{project.whyItMatters}
                    </Typography>
                  </Box>
                  <Paper
                    sx={{
                      p: 2.5,
                      bgcolor: alpha(ACCENT, 0.08),
                      border: `1px solid ${alpha(ACCENT, 0.3)}`,
                      minWidth: { md: 240 },
                      textAlign: "center",
                      borderRadius: 2,
                    }}
                  >
                    <Typography
                      variant="caption"
                      fontWeight={500}
                      sx={{ color: ACCENT, letterSpacing: "0.08em", display: "block", mb: 1 }}
                    >
                      {t("PortfolioDemoPage.headlineMetricLabel")}
                    </Typography>
                    <Typography
                      variant="h5"
                      fontWeight={500}
                      sx={{ color: DARK_TEXT, fontFamily: "'Fraunces', Georgia, serif", lineHeight: 1.2 }}
                    >
                      {project.headlineMetric}
                    </Typography>
                  </Paper>
                </Stack>
              </Paper>
            ))}
          </Stack>
        </Container>
      </Box>

      {/* Skills Matrix */}
      <Container maxWidth="lg" sx={{ py: { xs: 6, md: 10 } }}>
        <Stack spacing={1} mb={4}>
          <Typography variant="overline" fontWeight={700} sx={{ color: ACCENT, letterSpacing: "0.08em" }}>
            {t("PortfolioDemoPage.skillsMatrixEyebrow")}
          </Typography>
          <Typography
            variant="h3"
            fontWeight={500}
            sx={{
              fontFamily: "'Fraunces', Georgia, serif",
              letterSpacing: "-0.02em",
              color: DARK_TEXT,
            }}
          >
            {t("PortfolioDemoPage.skillsMatrixTitle")}
          </Typography>
          <Typography variant="body1" sx={{ color: alpha(DARK_TEXT, 0.7), maxWidth: 720, mt: 1 }}>
            {t("PortfolioDemoPage.skillsMatrixBody")}
          </Typography>
        </Stack>
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 1fr" }, gap: 3 }}>
          <SkillColumn
            label={t("PortfolioDemoPage.strongSkillsLabel")}
            color={theme.palette.success.light}
            skills={persona.strongSkills}
            footer={t("PortfolioDemoPage.strongSkillsFooter")}
          />
          <SkillColumn
            label={t("PortfolioDemoPage.moderateSkillsLabel")}
            color={theme.palette.warning.main}
            skills={persona.moderateSkills}
            footer={t("PortfolioDemoPage.moderateSkillsFooter")}
          />
          <SkillColumn
            label={t("PortfolioDemoPage.gapsLearningLabel")}
            color={DARK_MUTED}
            skills={persona.gapsInProgress}
            footer={t("PortfolioDemoPage.gapsLearningFooter")}
          />
        </Box>
      </Container>

      {/* Honest-Fit CTA */}
      <Box sx={{ bgcolor: alpha(ACCENT, 0.05), py: { xs: 6, md: 8 } }}>
        <Container maxWidth="md">
          <Stack spacing={2} alignItems="center" textAlign="center">
            <Typography
              variant="h3"
              fontWeight={500}
              sx={{
                fontFamily: "'Fraunces', Georgia, serif",
                letterSpacing: "-0.02em",
                color: DARK_TEXT,
                maxWidth: 700,
              }}
            >
              {t("PortfolioDemoPage.honestFitQuoteTitle")}
            </Typography>
            <Typography variant="h6" sx={{ color: alpha(DARK_TEXT, 0.75), fontWeight: 400, maxWidth: 620, lineHeight: 1.5 }}>
              {t("PortfolioDemoPage.honestFitBody")}
            </Typography>
            <Button
              variant="contained"
              size="large"
              sx={{
                mt: 2,
                bgcolor: ACCENT,
                color: DARK_BG,
                fontWeight: 500,
                px: 4,
                py: 1.5,
                "&:hover": { bgcolor: alpha(ACCENT, 0.85) },
              }}
            >
              {t("PortfolioDemoPage.startHonestFitAssessmentButton")}
            </Button>
          </Stack>
        </Container>
      </Box>

      {/* Footer — CareerForge attribution + build-your-own CTA */}
      <Box sx={{ bgcolor: DARK_SURFACE, py: 5, borderTop: `1px solid ${alpha(DARK_TEXT, 0.08)}` }}>
        <Container maxWidth="lg">
          <Stack direction={{ xs: "column", md: "row" }} spacing={3} alignItems="center" justifyContent="space-between">
            <Box>
              <Typography variant="body2" fontWeight={500} sx={{ color: DARK_TEXT, mb: 0.5 }}>
                {t("PortfolioDemoPage.footerTitle")}
              </Typography>
              <Typography variant="caption" sx={{ color: DARK_MUTED }}>
                {t("PortfolioDemoPage.footerBody")}
              </Typography>
            </Box>
            <Stack direction="row" spacing={1.5}>
              <Button
                variant="contained"
                onClick={() => navigate("/portfolio-kit/lovable-prompt")}
                sx={{ bgcolor: ACCENT, color: DARK_BG, fontWeight: 500, "&:hover": { bgcolor: alpha(ACCENT, 0.85) } }}
              >
                {t("PortfolioDemoPage.openPromptKitButton")}
              </Button>
              <Button
                variant="outlined"
                onClick={() => navigate("/portfolio-kit")}
                sx={{ borderColor: alpha(ACCENT, 0.5), color: ACCENT, "&:hover": { borderColor: ACCENT } }}
              >
                {t("PortfolioDemoPage.backToPortfolioKitButton")}
              </Button>
            </Stack>
          </Stack>
        </Container>
      </Box>
    </Box>
  );
}

// Simulated link-style button for the exemplar — these are demo links, not functional.
function LinkButton({ icon, label }: { icon: React.ReactNode; label: string }) {
  const theme = useTheme();
  const DARK_TEXT = theme.palette.background.default;
  const ACCENT = theme.palette.primary.main;
  return (
    <Button
      size="small"
      startIcon={icon}
      sx={{
        color: alpha(DARK_TEXT, 0.8),
        textTransform: "none",
        fontSize: "0.75rem",
        fontWeight: 500,
        px: 1.2,
        py: 0.3,
        border: `1px solid ${alpha(DARK_TEXT, 0.15)}`,
        borderRadius: 1.5,
        "&:hover": {
          borderColor: alpha(ACCENT, 0.4),
          color: ACCENT,
          bgcolor: alpha(ACCENT, 0.04),
        },
      }}
    >
      {label}
    </Button>
  );
}

function SkillColumn({
  label,
  color,
  skills,
  footer,
}: {
  label: string;
  color: string;
  skills: string[];
  footer: string;
}) {
  const theme = useTheme();
  const DARK_TEXT = theme.palette.background.default;
  return (
    <Paper
      sx={{
        p: 3,
        bgcolor: alpha(DARK_TEXT, 0.03),
        border: `1px solid ${alpha(color, 0.3)}`,
        borderTop: `3px solid ${color}`,
        borderRadius: 2,
      }}
    >
      <Typography
        variant="overline"
        fontWeight={800}
        sx={{ color, letterSpacing: "0.08em", display: "block", mb: 2 }}
      >
        {label}
      </Typography>
      <Stack spacing={1} mb={3}>
        {skills.map((s) => (
          <Typography key={s} variant="body2" sx={{ color: DARK_TEXT, lineHeight: 1.5 }}>
            {s}
          </Typography>
        ))}
      </Stack>
      <Divider sx={{ borderColor: alpha(DARK_TEXT, 0.1), my: 2 }} />
      <Typography variant="caption" sx={{ color: alpha(DARK_TEXT, 0.55), fontStyle: "italic", lineHeight: 1.5 }}>
        {footer}
      </Typography>
    </Paper>
  );
}
